/**
 * Branded Asset ID Types
 *
 * This module provides type-safe asset ID types for EVM and Solana chains.
 * Asset IDs combine an address with a chain ID in the format `{address}:{chainId}`.
 *
 * ## Format
 *
 * - EVM: `0x{40 hex chars}:{numeric chain ID}`
 *   - Example: `0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2:1`
 *
 * - Solana: `{32-44 base58 chars}:{1399811149}`
 *   - Example: `EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v:1399811149`
 *   - Solana chain ID is always 1399811149
 *
 * ## Design Philosophy
 *
 * These types solve the "stringly typed" problem where asset IDs were just
 * `${string}:${string}` with no compile-time safety. Now:
 *
 * 1. EVM and Solana asset IDs are distinct types
 * 2. Type guards enable exhaustive pattern matching
 * 3. Invalid formats are caught at compile time (for literals)
 * 4. The type system tracks validation proof through the codebase
 *
 * @module
 */

import type {
  Address,
  AddressChainType,
  EvmAddress,
  SolanaAddress,
} from "./address";
import {
  EVM_ADDRESS_REGEX,
  isEvmAddressFormat,
  isSolanaAddressFormat,
  SOLANA_ADDRESS_REGEX,
} from "./address";
import type { Brand } from "./branded";
import type {
  Base58Char,
  IsValidEvmAssetIdFormat,
  IsValidSolanaAssetIdFormat,
} from "./string-type-utils";

// =============================================================================
// Constants
// =============================================================================

/**
 * Solana's chain ID in our internal format.
 * This is a unique identifier chosen to not conflict with EVM chain IDs.
 */
export const SOLANA_CHAIN_ID = 1399811149 as const;

/**
 * Solana chain ID as a string (for template matching).
 */
export type SolanaChainIdString = "1399811149";

// =============================================================================
// Brand Symbols
// =============================================================================

/**
 * Unique symbol for branding EVM asset IDs.
 * @internal
 */
declare const __evmAssetIdBrand: unique symbol;

/**
 * Unique symbol for branding Solana asset IDs.
 * @internal
 */
declare const __solanaAssetIdBrand: unique symbol;

// =============================================================================
// Branded Asset ID Types
// =============================================================================

/**
 * A validated EVM asset ID.
 *
 * Format: `0x{40 hex chars}:{numeric chain ID}`
 * Example: `0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2:1`
 *
 * This branded type represents an asset ID that has been validated:
 * - Address is valid EVM format (0x + 40 hex)
 * - Chain ID is a valid numeric string
 * - Address is normalized to lowercase
 *
 * @example
 * ```typescript
 * const weth: EvmAssetId = parseEvmAssetId("0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2:1");
 * const usdc: EvmAssetId = parseEvmAssetId("0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48:1");
 * ```
 */
export type EvmAssetId = Brand<
  `0x${string}:${number}`,
  typeof __evmAssetIdBrand
>;

/**
 * A validated Solana asset ID.
 *
 * Format: `{32-44 base58 chars}:{1399811149}`
 * Example: `EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v:1399811149`
 *
 * This branded type represents an asset ID that has been validated:
 * - Address is valid Solana format (32-44 base58 chars)
 * - Chain ID is 1399811149 (our Solana chain ID)
 * - Address case is preserved (base58 is case-sensitive)
 *
 * @example
 * ```typescript
 * const usdc: SolanaAssetId = parseSolanaAssetId("EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v:1399811149");
 * const wsol: SolanaAssetId = parseSolanaAssetId("So11111111111111111111111111111111111111112:1399811149");
 * ```
 */
export type SolanaAssetId = Brand<
  `${string}:${typeof SOLANA_CHAIN_ID}`,
  typeof __solanaAssetIdBrand
>;

/**
 * Union of all supported asset ID types.
 *
 * Use type guards `isEvmAssetId` and `isSolanaAssetId` to narrow to specific types.
 *
 * @example
 * ```typescript
 * function getPrice(assetId: AssetId): number {
 *   if (isEvmAssetId(assetId)) {
 *     return getEvmPrice(assetId);  // TypeScript knows it's EvmAssetId
 *   } else {
 *     return getSolanaPrice(assetId);  // TypeScript knows it's SolanaAssetId
 *   }
 * }
 * ```
 */
export type AssetId = EvmAssetId | SolanaAssetId;

// =============================================================================
// Type-Level Validation (for literals)
// =============================================================================

/**
 * Compile-time validated EVM asset ID type.
 *
 * When used with a string literal, validates format at compile time.
 * Returns `never` if format is invalid.
 *
 * @template S - The string literal to validate
 */
export type ValidatedEvmAssetId<S extends string> =
  IsValidEvmAssetIdFormat<S> extends true ? S & EvmAssetId : never;

/**
 * Compile-time validated Solana asset ID type.
 *
 * When used with a string literal, validates format at compile time.
 * Returns `never` if format is invalid.
 *
 * @template S - The string literal to validate
 */
export type ValidatedSolanaAssetId<S extends string> =
  IsValidSolanaAssetIdFormat<S> extends true ? S & SolanaAssetId : never;

/**
 * Compile-time validated asset ID (EVM or Solana).
 *
 * Returns the appropriately branded type based on format validation.
 *
 * @template S - The string literal to validate
 */
export type ValidatedAssetId<S extends string> =
  IsValidEvmAssetIdFormat<S> extends true
    ? S & EvmAssetId
    : IsValidSolanaAssetIdFormat<S> extends true
      ? S & SolanaAssetId
      : never;

// =============================================================================
// Template Literal Types (partial compile-time safety)
// =============================================================================

/**
 * Template literal type for EVM asset IDs.
 * Catches obvious format errors at compile time.
 */
export type EvmAssetIdLike = `0x${string}:${number}`;

/**
 * Template literal type for Solana asset IDs.
 * Uses Base58 prefix check to catch EVM addresses used as Solana.
 */
export type SolanaAssetIdLike =
  `${Base58Char}${Base58Char}${string}:${SolanaChainIdString}`;

/**
 * Template literal union for any asset ID.
 */
export type AssetIdLike = EvmAssetIdLike | `${string}:${string}`;

// =============================================================================
// Regex Patterns
// =============================================================================

/**
 * Regex pattern for EVM asset IDs.
 * Format: 0x{40 hex}:{digits}
 */
export const EVM_ASSET_ID_REGEX = /^0x[a-fA-F0-9]{40}:\d+$/;

/**
 * Regex pattern for Solana asset IDs.
 * Format: {32-44 base58}:{digits}
 */
export const SOLANA_ASSET_ID_REGEX = /^[1-9A-HJ-NP-Za-km-z]{32,44}:\d+$/;

/**
 * Combined regex for any valid asset ID.
 */
export const ASSET_ID_REGEX =
  /^(0x[a-fA-F0-9]{40}:\d+|[1-9A-HJ-NP-Za-km-z]{32,44}:\d+)$/;

// =============================================================================
// Type Guards
// =============================================================================

/**
 * Type guard to check if a string is a valid EVM asset ID format.
 *
 * @param value - The string to check
 * @returns True if valid EVM asset ID format
 */
export function isEvmAssetIdFormat(value: string): value is EvmAssetId {
  return EVM_ASSET_ID_REGEX.test(value);
}

/**
 * Type guard to check if a string is a valid Solana asset ID format.
 *
 * @param value - The string to check
 * @returns True if valid Solana asset ID format
 */
export function isSolanaAssetIdFormat(value: string): value is SolanaAssetId {
  return SOLANA_ASSET_ID_REGEX.test(value);
}

/**
 * Type guard to check if a string is any valid asset ID format (v2 branded).
 *
 * @param value - The string to check
 * @returns True if valid asset ID format (EVM or Solana)
 */
export function isValidAssetId(value: string): value is AssetId {
  return ASSET_ID_REGEX.test(value);
}

/**
 * Type guard to check if an AssetId is an EvmAssetId.
 *
 * @param assetId - The asset ID to check
 * @returns True if the asset ID is an EVM asset ID
 */
export function isEvmAssetId(assetId: string): assetId is EvmAssetId {
  return (assetId as string).startsWith("0x");
}

/**
 * Type guard to check if an AssetId is a SolanaAssetId.
 *
 * @param assetId - The asset ID to check
 * @returns True if the asset ID is a Solana asset ID
 */
export function isSolanaAssetId(assetId: string): assetId is SolanaAssetId {
  return !isEvmAssetId(assetId);
}

// =============================================================================
// Parsed Asset ID Components
// =============================================================================

/**
 * Parsed components of an EVM asset ID.
 */
export interface ParsedEvmAssetId {
  readonly type: "evm";
  readonly address: EvmAddress;
  readonly chainId: number;
  readonly raw: EvmAssetId;
}

/**
 * Parsed components of a Solana asset ID.
 */
export interface ParsedSolanaAssetId {
  readonly type: "solana";
  readonly address: SolanaAddress;
  readonly chainId: typeof SOLANA_CHAIN_ID;
  readonly raw: SolanaAssetId;
}

/**
 * Parsed components of any asset ID.
 */
export type ParsedAssetId = ParsedEvmAssetId | ParsedSolanaAssetId;

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Gets the chain type for an asset ID.
 *
 * @param assetId - The asset ID
 * @returns The chain type ("evm" or "solana")
 */
export function getAssetIdChainType(assetId: AssetId): AddressChainType {
  return isEvmAssetId(assetId) ? "evm" : "solana";
}

/**
 * Extracts the address portion from an asset ID.
 *
 * @param assetId - The asset ID
 * @returns The address portion
 */
export function extractAddress(assetId: AssetId): Address {
  const colonIndex = (assetId as string).lastIndexOf(":");
  const addressPart = (assetId as string).slice(0, colonIndex);

  if (isEvmAssetId(assetId)) {
    return addressPart as EvmAddress;
  }
  return addressPart as SolanaAddress;
}

/**
 * Extracts the chain ID portion from an asset ID.
 *
 * @param assetId - The asset ID
 * @returns The chain ID as a number
 */
export function extractChainId(assetId: AssetId): number {
  const colonIndex = (assetId as string).lastIndexOf(":");
  return Number.parseInt((assetId as string).slice(colonIndex + 1), 10);
}

/**
 * Creates an asset ID from address and chain ID components.
 *
 * Note: This performs validation. For unvalidated inputs, use the parse functions.
 *
 * @param address - The validated address
 * @param chainId - The chain ID
 * @returns The combined asset ID
 */
export function createAssetId(address: EvmAddress, chainId: number): EvmAssetId;
export function createAssetId(
  address: SolanaAddress,
  chainId: typeof SOLANA_CHAIN_ID,
): SolanaAssetId;
export function createAssetId(address: Address, chainId: number): AssetId;
export function createAssetId(address: Address, chainId: number): AssetId {
  const raw = `${address}:${chainId}`;
  if (address.startsWith("0x")) {
    return raw as EvmAssetId;
  }
  return raw as SolanaAssetId;
}

/**
 * Normalizes an asset ID according to chain-specific rules.
 *
 * - EVM: address portion lowercased
 * - Solana: preserved as-is
 *
 * @param assetId - The asset ID to normalize
 * @returns The normalized asset ID
 */
export function normalizeAssetId<T extends AssetId | AssetIdLike>(assetId: T): T {
  if (isEvmAssetId(assetId)) {
    const colonIndex = (assetId as string).lastIndexOf(":");
    const address = (assetId as string).slice(0, colonIndex).toLowerCase();
    const chainId = (assetId as string).slice(colonIndex + 1);
    return `${address}:${chainId}` as T;
  }
  // Solana: case-sensitive, don't modify
  return assetId;
}

/**
 * Normalizes an asset ID string according to chain-specific rules.
 *
 * This is a convenience function for legacy code using `AssetIdFormat`
 * (the simple `${string}:${string}` template) instead of the branded `AssetId` type.
 *
 * - EVM (0x-prefixed): address portion lowercased
 * - Solana/other: preserved as-is (base58 is case-sensitive)
 *
 * @param assetId - The asset ID string to normalize
 * @returns The normalized asset ID string
 */
export function normalizeAssetIdFormat<
  T extends `${string}:${string}` | string,
>(assetId: T): T {
  // Check if it's an EVM asset ID (starts with 0x, case-insensitive)
  if ((assetId as string).toLowerCase().startsWith("0x")) {
    const colonIndex = (assetId as string).lastIndexOf(":");
    const address = (assetId as string).slice(0, colonIndex).toLowerCase();
    const chainId = (assetId as string).slice(colonIndex + 1);
    return `${address}:${chainId}` as T;
  }
  // Solana/non-EVM: case-sensitive, don't modify
  return assetId;
}

/**
 * Compares two asset IDs for equality.
 *
 * Uses chain-appropriate comparison:
 * - EVM: case-insensitive address comparison
 * - Solana: case-sensitive address comparison
 *
 * @param a - First asset ID
 * @param b - Second asset ID
 * @returns True if asset IDs are equal
 */
export function assetIdEquals(a: AssetId, b: AssetId): boolean {
  // Different chain types can't be equal
  if (isEvmAssetId(a) !== isEvmAssetId(b)) {
    return false;
  }

  if (isEvmAssetId(a)) {
    return (a as string).toLowerCase() === (b as string).toLowerCase();
  }

  // Solana: case-sensitive comparison
  return a === b;
}

// =============================================================================
// Exhaustiveness Helper
// =============================================================================

/**
 * Helper for exhaustive pattern matching.
 *
 * Use in switch statements to ensure all cases are handled.
 * TypeScript will error if a case is missing.
 *
 * @example
 * ```typescript
 * function process(assetId: AssetId) {
 *   if (isEvmAssetId(assetId)) {
 *     return handleEvm(assetId);
 *   } else if (isSolanaAssetId(assetId)) {
 *     return handleSolana(assetId);
 *   }
 *   return assertNever(assetId);  // Error if new chain type added
 * }
 * ```
 */
export function assertNever(value: never): never {
  throw new Error(`Unexpected value: ${value}`);
}
